import React, { useState } from 'react';
import isEqual from 'lodash.isequal';
import { Card } from './card';
import { ReactComponent as EditIcon } from './../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from './../../../assets/icons/delete.svg';
import { ReactComponent as BranchIcon } from './../../../assets/icons/branch.svg';
import { ReactComponent as SaveIcon } from './../../../assets/icons/save.svg';
import { ReactComponent as BackIcon } from './../../../assets/icons/back.svg';
import { Button } from '../inputs/button';
import { useNavigate } from 'react-router-dom';
import { More } from '../contextMenu/more';
import { DeleteResource } from '../actions/deleteResource';

import styles from './scheduleCard.module.css';
import { Load } from '../load/load';
import { Api } from '../../../services/api';
import { useEffect } from 'react';
import { Notify } from '../../../services/notification/notify';
import { LoadText } from '../load/loadText';
import { IconSwapper } from '../icons/iconSwapper';


export const ScheduleCard = ({
    title = <></>,
    editButton = false,
    deleteButton = false,
    branchId = null,
    loading = false,
    schedule = {
        id: null,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false
    },
    editMode = false,
    onChange = v => {},
    refreshData = () => {}
}) => {

    const navigate = useNavigate();

    const [isEditing, setIsEditing] = useState(editMode);
    const [isSavingEdits, setIsSavingEdits] = useState(false);
    const [promptDelete, setPromptDelete] = useState(false);
    const [data, setData] = useState(schedule);

    useEffect(() => {
        setIsEditing(editMode);
    }, [editMode]);

    useEffect(() => {
        onChange?.(data);
    }, [data])

    useEffect(() => {
        if (!isEqual(schedule, data)) {
            setData(schedule);
        }
    }, [schedule]);

    const actions = () => {
        const a = [];
        if (branchId)
            a.push({
                label: 'View Branch',
                icon: <BranchIcon />,
                onClick: () => navigate('/locations/branches/' + branchId)
            });

        if (branchId && (editButton || deleteButton))
            a.push({ isSpacer: true });

        if (editButton)
            a.push({
                label: 'Edit Schedule',
                icon: <EditIcon />,
                onClick: () => {setIsEditing(true);}
            });

        if (deleteButton && schedule.id)
            a.push({
                label: 'Delete Schedule',
                icon: <DeleteIcon />,
                onClick: () => {setPromptDelete(true);}
            });

        return a;
    }

    const saveSchedule = () => {
        setIsSavingEdits(true);

        Api.patch('/locations/schedules/' + schedule.id, data)
        .then(json => {
            if (!json.success)
                throw new Error('Request was not successful');

            setIsSavingEdits(false);
            refreshData();
        })
        .catch(err => {
            Notify.error('Error Saving Schedule', 'There was an error saving the schedule.');
            console.error(err);
            setData(schedule);
            setIsSavingEdits(false);
        })
    }

    const handleScheduleChange = (day) => {
        setData({
            ...data,
            [day]: !data[day]
        });
    }

    return <Card className={styles.ScheduleCard}>
        {
            title && title != <></> ?
            <div className={styles.Title}>
                <h1>{title}</h1>
                <IconSwapper
                    icons={[
                        {
                            icon: <div className={styles.More}>
                                {(actions().length > 0) ?
                                <More actions={actions()} />
                                : ''}
                            </div>,
                            exit: 'top',
                            active: !isEditing && !isSavingEdits
                        },
                        {
                            icon: <div className={styles.EditActions}>
                                <Button
                                    className={styles.Btn + ' ' + styles.SaveBtn}
                                    onClick={() => { setIsEditing(false); saveSchedule();}}
                                    disabled={isSavingEdits}
                                ><div className='icon'><SaveIcon /></div></Button>
                                <Button
                                    className={styles.Btn + ' ' + styles.BackBtn}
                                    onClick={() => { setIsEditing(false); setData(schedule); }}
                                    disabled={isSavingEdits}
                                ><div className='icon'><BackIcon /></div></Button>
                            </div>,
                            exit: 'right',
                            active: isEditing && schedule.id
                        },
                        {
                            icon: <div className={styles.SavingAction}>
                                <div className={styles.SaveLoading}><Load /></div>
                            </div>,
                            exit: 'top',
                            active: isSavingEdits
                        }
                    ]}
                />
            </div>
            : ''
        }

        <DeleteResource
            resourceName='Schedule'
            name={title}
            restEndpoint={'locations/schedules'}
            // @ts-ignore
            ids={[schedule.id]}
            successCallback={() => {
                setPromptDelete(false);
                refreshData();
            }}
            errorCallback={() => setPromptDelete(false)}
            cancelCallback={() => setPromptDelete(false)}
            prompt={promptDelete}
        />

        <div className={styles.Schedule}>
            <ScheduleDay 
                day='Mon'
                active={data.monday}
                loading={loading}
                setActive={() => handleScheduleChange('monday')}
                editable={isEditing}
            />
            <ScheduleDay 
                day='Tue'
                active={data.tuesday}
                loading={loading}
                setActive={() => handleScheduleChange('tuesday')}
                editable={isEditing}
            />
            <ScheduleDay 
                day='Wed'
                active={data.wednesday}
                loading={loading}
                setActive={() => handleScheduleChange('wednesday')}
                editable={isEditing}
            />
            <ScheduleDay 
                day='Thu'
                active={data.thursday}
                loading={loading}
                setActive={() => handleScheduleChange('thursday')}
                editable={isEditing}
            />
            <ScheduleDay 
                day='Fri'
                active={data.friday}
                loading={loading}
                setActive={() => handleScheduleChange('friday')}
                editable={isEditing}
            />
        </div>
    </Card>
}

const ScheduleDay = ({day, active, setActive, loading, editable}) => {

    const handleClick = () => {
        if (editable)
            setActive(!active);
    }

    return loading ?
        <LoadText min={100} max={100} height='51px' />
    :
    <Button
        interactive={editable}
        className={styles.ScheduleDayBtn + (active ? ' ' + styles.Active : '')}
        onClick={() => handleClick()}
        rippleColor={(!active) ? 'var(--primary-color)' : 'var(--secondary-light-color)'}
        rippleFade={false}
        multipleRipples={false}
    >{day}</Button>
}